import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Store, Plus, Trash2, Package, DollarSign, Clock } from "lucide-react";
import { toast } from "sonner";

const CATS = ["rudraksha", "yantra", "gemstone", "book", "incense", "samagri", "digital"];

export default function VendorDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", category: "rudraksha", price: "", description: "", image: "", stock: 50 });

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (!user) return;
    if (user.role !== "vendor") { nav("/dashboard"); return; }
    load();
  }, [user, nav]);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([api.get("/vendor/dashboard"), api.get("/vendor/products")]);
      setStats(s.data);
      setProducts(p.data);
    } catch (e) { console.error(e); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vendor/products", { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) });
      toast.success("Product submitted for admin approval");
      setShowForm(false);
      setForm({ name: "", category: "rudraksha", price: "", description: "", image: "", stock: 50 });
      await load();
    } catch { toast.error("Failed to add product"); }
  };

  const remove = async (pid) => {
    if (!confirm("Delete this product?")) return;
    try {
      await api.delete(`/vendor/products/${pid}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Failed"); }
  };

  if (!stats) return <div className="p-12 text-center text-[#A0A4AB]">Loading vendor console…</div>;

  const Stat = ({ label, value, icon: Ic }) => (
    <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
      <Ic className="text-[#D4AF37]" size={18}/>
      <div className="font-serif text-3xl mt-3">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB] mt-1">{label}</div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 pb-24 lg:pb-12">
      <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Vendor Studio</div>
          <h1 className="font-serif text-4xl">Namaste, <span className="gold-gradient">{user.name}</span></h1>
        </div>
        <button
          data-testid="vendor-add-product-btn"
          onClick={() => setShowForm(true)}
          className="px-5 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition inline-flex items-center gap-2"
        >
          <Plus size={16}/> Add Product
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Stat label="Total Products" value={stats.products} icon={Package}/>
        <Stat label="Approved" value={stats.approved} icon={Store}/>
        <Stat label="Pending" value={stats.pending} icon={Clock}/>
        <Stat label="Revenue" value={`₹${stats.revenue.toLocaleString("en-IN")}`} icon={DollarSign}/>
      </div>

      <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
        <div className="font-serif text-xl mb-4">Your Products</div>
        {products.length === 0 ? (
          <div className="py-8 text-center text-[#A0A4AB]">No products yet. Click "Add Product" above.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="p-4 rounded-lg border border-white/10 bg-[#050507]">
                {p.image && <img src={p.image} className="w-full h-32 object-cover rounded mb-3"/>}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm truncate">{p.name}</div>
                    <div className="text-xs text-[#D4AF37] mt-1">₹{p.price}</div>
                  </div>
                  <button data-testid={`vendor-delete-${p.id}`} onClick={() => remove(p.id)} className="text-[#FF5A36] hover:text-red-400">
                    <Trash2 size={14}/>
                  </button>
                </div>
                <div className="mt-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
                    p.approved ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                               : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/40"
                  }`}>
                    {p.approved ? "Approved" : "Pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add product modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0A0C10] border border-white/10 rounded-xl p-8 max-w-lg w-full space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#D4AF37]">New Product</div>
              <h3 className="font-serif text-2xl mt-1">Add to marketplace</h3>
            </div>
            <div>
              <label className="text-xs text-[#A0A4AB]">Product Name</label>
              <input data-testid="vp-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#A0A4AB]">Category</label>
                <select data-testid="vp-category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none">
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#A0A4AB]">Price (₹)</label>
                <input data-testid="vp-price" required type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#A0A4AB]">Image URL</label>
              <input data-testid="vp-image" required value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none" placeholder="https://…"/>
            </div>
            <div>
              <label className="text-xs text-[#A0A4AB]">Description</label>
              <textarea data-testid="vp-desc" required rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 w-full px-3 py-2 rounded bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"/>
            </div>
            <div className="flex gap-3">
              <button type="submit" data-testid="vp-submit" className="flex-1 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition">Submit for approval</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 rounded-full border border-white/15 text-sm hover:border-white/30">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
